import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [faqOpen, setFaqOpen] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});
  const canvasRef = useRef(null);

  const toggleFaq = (index) => setFaqOpen(faqOpen === index ? null : index);

  // Particle background canvas (same as About/Services)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(100, Math.floor(window.innerWidth / 20));
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.2,
          alpha: Math.random() * 0.5 + 0.2,
        });
      }
    };
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 217, 102, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });
      animationId = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', resize);
    resize();
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.2 }
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const deepBlue = '#0A2F44';
  const silver = '#F0F4F8';
  const gold = '#FFD966';

  const styles = {
    pageWrapper: {
      backgroundColor: deepBlue,
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden',
    },
    canvas: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 0,
    },
    content: {
      position: 'relative',
      zIndex: 1,
    },
    hero: {
      position: 'relative',
      color: '#FFFFFF',
      padding: '0',
      textAlign: 'center',
      height: '80vh',
      minHeight: '500px',
      overflow: 'hidden',
    },
    heroVideo: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      zIndex: 0,
    },
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      zIndex: 1,
    },
    heroContent: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    heroTitle: {
      fontSize: '3rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
      animation: 'fadeInUp 1s ease',
      color: '#FFFFFF',
      textShadow: '0 0 8px rgba(240,244,248,0.3)',
    },
    heroSubtitle: {
      fontSize: '1.25rem',
      marginBottom: '2rem',
      color: silver,
    },
    btnPrimary: {
      display: 'inline-block',
      backgroundColor: gold,
      color: deepBlue,
      fontWeight: 'bold',
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      textDecoration: 'none',
      marginRight: '1rem',
      transition: 'all 0.3s',
      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
    },
    btnSecondary: {
      display: 'inline-block',
      backgroundColor: 'transparent',
      border: `2px solid ${gold}`,
      color: gold,
      fontWeight: 'bold',
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      textDecoration: 'none',
      transition: 'all 0.3s',
    },
    section: {
      padding: '4rem 1rem',
      backgroundColor: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(8px)',
      borderRadius: '24px',
      margin: '2rem 0',
      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
      opacity: 0,
      transform: 'translateY(30px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    sectionVisible: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 1rem',
    },
    sectionTitle: {
      fontSize: '2rem',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '1rem',
      color: '#FFFFFF',
      textShadow: '0 0 8px rgba(240,244,248,0.3)',
    },
    sectionSubtitle: {
      fontSize: '1.125rem',
      textAlign: 'center',
      color: silver,
      maxWidth: '600px',
      margin: '0 auto 3rem auto',
    },
    grid: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '2rem',
      marginBottom: '4rem',
    },
    card: {
      flex: '1 1 300px',
      backgroundColor: 'rgba(0,0,0,0.3)',
      backdropFilter: 'blur(4px)',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      padding: '1.5rem',
      textAlign: 'center',
      transition: 'transform 0.3s, box-shadow 0.3s',
      cursor: 'pointer',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    cardIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
      color: gold,
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      marginBottom: '0.5rem',
      color: '#FFFFFF',
    },
    cardText: {
      color: silver,
    },
    twoCol: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '3rem',
      marginBottom: '4rem',
    },
    colHalf: {
      flex: '1 1 300px',
    },
    image: {
      width: '100%',
      borderRadius: '16px',
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
      transition: 'transform 0.3s',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    list: {
      listStyle: 'none',
      padding: 0,
    },
    listItem: {
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: silver,
    },
    checkMark: {
      color: gold,
      fontWeight: 'bold',
      fontSize: '1.25rem',
    },
    initiativeGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '2rem',
      marginBottom: '4rem',
    },
    initiativeCard: {
      flex: '1 1 280px',
      backgroundColor: 'rgba(0,0,0,0.3)',
      backdropFilter: 'blur(4px)',
      borderRadius: '16px',
      padding: '1.5rem',
      textAlign: 'center',
      transition: 'transform 0.3s, box-shadow 0.3s',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    initiativeIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
      color: gold,
    },
    initiativeTitle: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      marginBottom: '0.5rem',
      color: '#FFFFFF',
    },
    initiativeDesc: {
      color: silver,
      marginBottom: '1rem',
    },
    initiativeBadge: {
      position: 'absolute',
      top: 10,
      right: 10,
      backgroundColor: gold,
      color: deepBlue,
      padding: '0.25rem 0.5rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
    },
    faqItem: {
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      marginBottom: '1rem',
    },
    faqQuestion: {
      padding: '1rem',
      cursor: 'pointer',
      fontWeight: 'bold',
      color: '#FFFFFF',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'color 0.2s',
    },
    faqAnswer: {
      padding: '0 1rem 1rem 1rem',
      color: silver,
      display: 'none',
    },
    scrollingTextBanner: {
      backgroundColor: 'rgba(0,0,0,0.4)',
      backdropFilter: 'blur(4px)',
      padding: '0.8rem 0',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      margin: '1rem 0',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    scrollingText: {
      display: 'inline-block',
      animation: 'scrollRightToLeft 25s linear infinite',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: gold,
    },
    footer: {
      backgroundColor: 'rgba(0,0,0,0.4)',
      backdropFilter: 'blur(8px)',
      color: silver,
      padding: '3rem 1rem',
      borderRadius: '24px',
      marginTop: '3rem',
      textAlign: 'center',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    footerContent: {
      maxWidth: '800px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2,
    },
    footerTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
      color: '#FFFFFF',
    },
    footerContact: {
      fontSize: '1.125rem',
      lineHeight: '1.8',
      marginBottom: '1.5rem',
    },
    footerButton: {
      backgroundColor: gold,
      color: deepBlue,
      padding: '0.75rem 2rem',
      borderRadius: '50px',
      textDecoration: 'none',
      fontSize: '1rem',
      display: 'inline-block',
      transition: 'transform 0.2s, background-color 0.2s',
      marginTop: '1rem',
      fontWeight: 'bold',
    },
    flyingEmojisContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 1,
    },
    flyingEmoji: {
      position: 'absolute',
      bottom: '-20px',
      fontSize: '1.8rem',
      opacity: 0.8,
      pointerEvents: 'none',
      animation: 'floatUp linear infinite',
    },
  };

  const animationStyle = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      background-color: ${deepBlue};
      margin: 0;
      padding: 0;
    }
    html {
      font-size: 90%;
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes scrollRightToLeft {
      0% { transform: translateX(100%); }
      100% { transform: translateX(-100%); }
    }
    @keyframes floatUp {
      0% {
        transform: translateY(0) rotate(0deg);
        opacity: 0.9;
      }
      100% {
        transform: translateY(-350px) rotate(15deg);
        opacity: 0;
      }
    }
    .hover-lift:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 24px rgba(0,0,0,0.3);
    }
    .hover-zoom:hover img {
      transform: scale(1.05);
    }
    .initiative-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 24px rgba(0,0,0,0.3);
      background: rgba(0,0,0,0.4);
    }
    .initiative-card:hover .initiative-icon {
      transform: scale(1.1);
    }
    .footer-hover:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 30px -10px rgba(0,0,0,0.4);
    }
    .btn-primary:hover {
      background-color: #FFFFFF;
      color: ${deepBlue};
    }
    .btn-secondary:hover {
      background-color: ${gold};
      color: ${deepBlue};
      border-color: ${gold};
    }
  `;

  const initiatives = [
    {
      icon: '🌿',
      title: 'Eco‑Friendly Cleaning',
      desc: 'We use biodegradable solutions and vacuum trucks that minimise environmental impact.',
      badge: 'Green',
    },
    {
      icon: '💧',
      title: 'Water Conservation',
      desc: 'Our methods help protect groundwater and reduce wastewater contamination.',
      badge: 'Sustainable',
    },
    {
      icon: '🏡',
      title: 'Healthy Homes Initiative',
      desc: 'Free community workshops on proper septic care to prevent health hazards.',
      badge: 'Awareness',
    },
  ];

  // Emojis for flying animation
  const flyingEmojisList = ['💧', '🌿', '✅', '🌟', '🔄', '🔍', '🛠️', '💚', '🌱', '💦', '🏡', '♻️'];

  return (
    <div style={styles.pageWrapper}>
      <style>{animationStyle}</style>
      <canvas ref={canvasRef} style={styles.canvas} />
      <div style={styles.content}>
        {/* Hero Section */}
        <section style={styles.hero}>
          <video autoPlay muted loop style={styles.heroVideo}>
            <source src="https://media.istockphoto.com/id/2157889334/video/wastewater-pump-is-lowered-into-septic-tank-to-pump-sewage.mp4?s=mp4-640x640-is&k=20&c=QUFuU6k9XxvvE4zCZQRtJtobBbB3kJ88o85WlYTiA70=" type="video/mp4" />
          </video>
          <div style={styles.heroOverlay}></div>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>Varahi septic cleaning services</h1>
            <p style={styles.heroSubtitle}>
              Professional septic cleaning that safeguards your home and the planet.
            </p>
            <div>
              <Link to="/contact" style={styles.btnPrimary} className="btn-primary hover-lift">
                Get a Free Quote
              </Link>
              <Link to="/services" style={styles.btnSecondary} className="btn-secondary hover-lift">
                Our Services
              </Link>
            </div>
          </div>
        </section>

        {/* Scrolling Text Banner */}
        <div style={styles.scrollingTextBanner}>
          <div style={styles.scrollingText}>
            🌟 Special Offer: Free inspection with every septic pumping! 🌟 Call us today! &nbsp;&nbsp;|&nbsp;&nbsp;
            💧 Eco-friendly solutions &nbsp;&nbsp;|&nbsp;&nbsp;
            ✅ 24/7 Emergency service &nbsp;&nbsp;|&nbsp;&nbsp;
            📞 9948803051 &nbsp;&nbsp;|&nbsp;&nbsp;
            🌿 Protecting your home and the environment
          </div>
        </div>

        {/* Services Cards */}
        <section
          id="services"
          className="animate-on-scroll"
          style={{ ...styles.section, ...(visibleSections.services ? styles.sectionVisible : {}) }}
        >
          <div style={styles.container}>
            <h2 style={styles.sectionTitle}>Complete Septic Solutions</h2>
            <p style={styles.sectionSubtitle}>
              From routine pumping to emergency repairs, we handle all your septic needs.
            </p>
            <div style={styles.grid}>
              {[
                { icon: '🔄', title: 'Septic Tank Pumping', text: 'Regular pumping prevents backups and extends system life. Recommended every 3-5 years.' },
                { icon: '🔍', title: 'Inspections & Camera', text: 'Video camera inspections diagnose issues before they become emergencies.' },
                { icon: '🛠️', title: 'Installation & Repair', text: 'Expert installation of new systems and fast, reliable repairs for any problem.' },
              ].map((service, idx) => (
                <div key={idx} style={styles.card} className="hover-lift">
                  <div style={styles.cardIcon}>{service.icon}</div>
                  <h3 style={styles.cardTitle}>{service.title}</h3>
                  <p style={styles.cardText}>{service.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Green Initiatives Section */}
        <section
          id="initiatives"
          className="animate-on-scroll"
          style={{ ...styles.section, ...(visibleSections.initiatives ? styles.sectionVisible : {}) }}
        >
          <div style={styles.container}>
            <h2 style={styles.sectionTitle}>Our Commitment to a Cleaner Environment</h2>
            <p style={styles.sectionSubtitle}>
              We go beyond cleaning – we actively protect natural resources and promote healthy communities.
            </p>
            <div style={styles.initiativeGrid}>
              {initiatives.map((initiative, idx) => (
                <div
                  key={idx}
                  style={styles.initiativeCard}
                  className="initiative-card"
                >
                  <div style={styles.initiativeBadge}>{initiative.badge}</div>
                  <div style={styles.initiativeIcon}>{initiative.icon}</div>
                  <h3 style={styles.initiativeTitle}>{initiative.title}</h3>
                  <p style={styles.initiativeDesc}>{initiative.desc}</p>
                  <Link to="/contact" style={{ ...styles.btnPrimary, fontSize: '0.9rem', padding: '0.5rem 1rem' }}>Learn More</Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section
          id="whyus"
          className="animate-on-scroll"
          style={{ ...styles.section, ...(visibleSections.whyus ? styles.sectionVisible : {}) }}
        >
          <div style={styles.container}>
            <div style={styles.twoCol}>
              <div style={styles.colHalf} className="hover-zoom">
                <img
                  src="https://www.biocleanseptic.in/cdn/shop/articles/Cleaning_the_septic_tank_pumping_out_574ac9aa-6a3a-484b-a82c-08abf84ec504.webp?v=1755686933&width=1100"
                  alt="Professional drainage cleaning service"
                  style={styles.image}
                />
              </div>
              <div style={styles.colHalf}>
                <h2 style={{ ...styles.sectionTitle, textAlign: 'left' }}>Why Homeowners Trust Us</h2>
                <ul style={styles.list}>
                  <li style={styles.listItem}><span style={styles.checkMark}>✓</span> 20+ years combined experience</li>
                  <li style={styles.listItem}><span style={styles.checkMark}>✓</span> Licensed, insured & bonded</li>
                  <li style={styles.listItem}><span style={styles.checkMark}>✓</span> Free estimates & upfront pricing</li>
                  <li style={styles.listItem}><span style={styles.checkMark}>✓</span> Emergency service 24/7</li>
                  <li style={styles.listItem}><span style={styles.checkMark}>✓</span> Eco‑friendly practices</li>
                  <li style={styles.listItem}><span style={styles.checkMark}>✓</span> Community support programs</li>
                </ul>
                <Link to="/about" style={{ ...styles.btnPrimary, borderRadius: '50px' }} className="hover-lift">Learn More</Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          id="faq"
          className="animate-on-scroll"
          style={{ ...styles.section, ...(visibleSections.faq ? styles.sectionVisible : {}) }}
        >
          <div style={styles.container}>
            <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
            <div>
              {[
                { q: "How often should I pump my septic tank?", a: "We recommend every 3-5 years depending on household size and usage." },
                { q: "What are signs of a failing septic system?", a: "Slow drains, gurgling sounds, odors, and wet spots in the yard are common indicators." },
                { q: "Do you offer emergency services?", a: "Yes, we offer 24/7 emergency services for urgent repairs and backups." },
                { q: "Are your cleaning methods environmentally safe?", a: "Absolutely. We use eco‑friendly solutions and safe disposal practices." },
              ].map((faq, idx) => (
                <div key={idx} style={styles.faqItem}>
                  <div style={styles.faqQuestion} onClick={() => toggleFaq(idx)}>
                    {faq.q}
                    <span style={{ fontSize: '1.5rem' }}>{faqOpen === idx ? '−' : '+'}</span>
                  </div>
                  <div style={{ ...styles.faqAnswer, display: faqOpen === idx ? 'block' : 'none' }}>
                    {faq.a}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer with Contact Details and Flying Emojis */}
        <footer
          className="animate-on-scroll footer-hover"
          id="footer"
          style={{
            ...styles.footer,
            ...(visibleSections.footer ? { transform: 'translateY(0)', opacity: 1 } : { transform: 'translateY(20px)', opacity: 0, transition: 'all 0.6s ease' }),
          }}
        >
          {/* Flying emojis animation */}
          <div style={styles.flyingEmojisContainer}>
            {flyingEmojisList.map((emoji, idx) => {
              const randomLeft = Math.random() * 90 + 5; // 5% to 95%
              const randomDuration = Math.random() * 5 + 4; // 4s to 9s
              const randomDelay = Math.random() * 8; // 0s to 8s
              return (
                <span
                  key={idx}
                  style={{
                    ...styles.flyingEmoji,
                    left: `${randomLeft}%`,
                    animationDuration: `${randomDuration}s`,
                    animationDelay: `${randomDelay}s`,
                  }}
                >
                  {emoji}
                </span>
              );
            })}
          </div>
          <div style={styles.footerContent}>
            <h3 style={styles.footerTitle}>Get in Touch</h3>
            <div style={styles.footerContact}>
              <p>
                📞 Phone: <strong><a href="tel:9948803051" style={{ color: gold, textDecoration: 'none' }}>9948803051</a></strong>
              </p>
              <p>
                ✉️ Email: <strong><a href="mailto:varahisepticservices@gmail.com" style={{ color: gold, textDecoration: 'none' }}>varahisepticservices@gmail.com</a></strong>
              </p>
              <p>
                📍 Branches: <strong>Madhurawada, Maddilapalem, Thatichetlapalem, Portarea, Vepagunta, Sheelanagar, Gajuwaka, Aganampudi, Pendurthi.</strong>
              </p>
            </div>
            <Link to="/contact" style={styles.footerButton} className="hover-lift">   
              Contact Us
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home;